import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { MaskedLine } from './Motion';

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start'],
    });

    const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
    const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-15%']);
    const orbY = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);

    return (
        <section
            ref={ref}
            className="relative min-h-[100svh] pt-32 pb-16 overflow-hidden"
            data-testid="hero-section"
        >
            {/* Ambient orb */}
            <motion.div
                style={{
                    y: orbY,
                    width: 600,
                    height: 600,
                    top: '30%',
                    insetInlineStart: '10%',
                }}
                className="hero-orb bg-emerald-signal"
                aria-hidden
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.35 }}
                transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
            />

            <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] mb-10 text-ink-600"
                    data-testid="hero-eyebrow"
                >
                    <span className="w-8 h-px bg-ink-600" />
                    <span>الإصدار 01 · دليل مختار بعناية</span>
                </motion.div>

                <motion.h1
                    style={{ y: textY }}
                    className="font-display font-black text-[11vw] md:text-[6.5vw] leading-[1] tracking-tight"
                    data-testid="hero-title"
                >
                    <span className="block">
                        <MaskedLine delay={0.5}>استطلاعات</MaskedLine>
                    </span>
                    <span className="block italic font-light">
                        <MaskedLine delay={0.7}>تُدفع لك</MaskedLine>
                    </span>
                    <span className="block">
                        <span className="text-emerald-signal">
                            <MaskedLine delay={0.9}>·</MaskedLine>
                        </span>{' '}
                        <MaskedLine delay={0.95}>بالوقت</MaskedLine>{' '}
                        <span className="text-ink-400">
                            <MaskedLine delay={1.05}>والحقيقة</MaskedLine>
                        </span>
                    </span>
                </motion.h1>

                <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    {/* Description */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-5 lg:col-start-1"
                    >
                        <p className="text-base md:text-lg leading-relaxed text-ink-600 max-w-md">
                            دليل مُنسّق يدويًا لأفضل الاستطلاعات المدفوعة في العالم العربي.
                            كل رابط تمّت مراجعته، كل مكافأة تمّ التحقق منها. لا ضجيج، لا وعود مبالغ فيها.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href="#flagship"
                                className="hover-arrow inline-flex items-center gap-2 px-6 py-3 bg-ink text-sand-100 text-sm font-medium hover:bg-emerald-signal transition-colors duration-500"
                                data-testid="hero-cta-primary"
                            >
                                العرض المميز
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path
                                        d="M13 7L1 7M1 7L7 1M1 7L7 13"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    />
                                </svg>
                            </a>
                            <a
                                href="#directory"
                                className="hover-arrow inline-flex items-center gap-2 px-6 py-3 border border-ink text-sm font-medium hover:bg-ink hover:text-sand-100 transition-colors duration-500"
                                data-testid="hero-cta-secondary"
                            >
                                تصفح الدليل
                            </a>
                        </div>
                    </motion.div>

                    {/* Hero image with clipped spotlight */}
                    <motion.div
                        style={{ y: imageY }}
                        className="lg:col-span-5 lg:col-start-8 relative"
                    >
                        <motion.div
                            initial={{ clipPath: 'inset(100% 0 0 0)' }}
                            animate={{ clipPath: 'inset(0% 0 0 0)' }}
                            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
                            className="relative aspect-[4/5] w-full max-w-md ms-auto overflow-hidden border border-ink"
                        >
                            <img
                                src="https://images.pexels.com/photos/30256526/pexels-photo-30256526.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt=""
                                className="w-full h-full object-cover grayscale contrast-125"
                            />
                            <div className="absolute inset-0 bg-emerald-signal mix-blend-multiply opacity-10" />
                            <div className="absolute bottom-4 start-4 text-sand-100 text-[10px] uppercase tracking-[0.3em] mix-blend-difference">
                                Fig. 01 — المشترك
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Stats row */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.6 }}
                    className="mt-20 pt-8 border-t border-ink/20 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm"
                >
                    {[
                        ['+120', 'استطلاع نشط'],
                        ['12K+', 'مشترك عربي'],
                        ['$50', 'متوسط المكافأة'],
                        ['24/7', 'دفع فوري'],
                    ].map(([n, l], i) => (
                        <div key={i} data-testid={`hero-stat-${i}`}>
                            <div className="font-display font-black text-3xl md:text-4xl">{n}</div>
                            <div className="text-ink-400 mt-2 uppercase tracking-widest text-[10px]">
                                {l}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
